/**
 * useInlineEdit Hook
 * Manages inline cell editing for dashboard tables
 */
import { useState, useCallback, useRef, useEffect } from 'react';

export const useInlineEdit = ({
  onSave,
  onCancel
}) => {
  const [editCell, setEditCell] = useState(null); // { rowId, field, value }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Start editing a cell
  const startEdit = useCallback((rowId, field, currentValue) => {
    setEditCell({ rowId, field, value: currentValue });
    setEditValue(currentValue || '');
  }, []);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    setEditCell(null);
    setEditValue('');
    if (onCancel) onCancel();
  }, [onCancel]);

  // Save edit
  const saveEdit = useCallback(async () => {
    if (!editCell) return;
    
    // Don't save if value hasn't changed
    if (editValue === editCell.value) {
      cancelEdit();
      return;
    }

    setSaving(true);
    try {
      await onSave(editCell.rowId, editCell.field, editValue);
      setEditCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  }, [editCell, editValue, onSave, cancelEdit]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }, [saveEdit, cancelEdit]);

  // Check if a cell is being edited
  const isEditing = useCallback((rowId, field) => {
    return editCell?.rowId === rowId && editCell?.field === field;
  }, [editCell]);

  // Focus input when editing starts
  useEffect(() => {
    if (editCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editCell]);

  return {
    editCell,
    editValue,
    setEditValue,
    saving,
    inputRef,
    startEdit,
    cancelEdit,
    saveEdit,
    handleKeyDown,
    isEditing
  };
};

export default useInlineEdit;
